import {useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {useAuthUserMutation, useSetKundUserMutation} from '../api/AuthApi/authService';
import {useLazyGetUserViewQuery} from '../api/UserApi/userView';
import {logOut, setCredentials, setIsAuth, setIsAuthLoading} from '../store/reducers/authSlice';
import {setActiveRole, setUser} from '../store/reducers/userSlice';
import {useKundelikAuth} from '../utils/hooks/useKundelikAuth';
import {setIsError} from '../store/reducers/notificationSlice';
import {useParam} from '../utils/hooks/useParam';
import {useTranslation} from 'react-i18next';

export const useAuth = () => {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [authUser] = useAuthUserMutation();
    const [getUserInfo] = useLazyGetUserViewQuery();
    const isAuth = useSelector((state) => state.auth.isAuth);

    const {getUrlParam} = useParam();

    const url = new URL(window.location.href);
    const accessToken = url.hash?.split('&').find(param => param.startsWith('access_token'))?.split('=')[1];

    useKundelikAuth(accessToken);

    const [setKundUser] = useSetKundUserMutation();

    const linkKundUser = async (code) => {
        const result = await setKundUser({
            code
        });
        if (result.error) {
            dispatch(setIsError(t(`general.models.serverErrors.setKundUser.${result.error.data.error_code}`)));
        } else {
            dispatch(setUser({kund_user_id: code}));
        }
    };

    useEffect(() => {
        const code = getUrlParam('code');
        if (code) {
            linkKundUser(code);
        }
    }, []);

    const storedUser = JSON.parse(localStorage.getItem('user'));
    const refresh_token = JSON.parse(localStorage.getItem('jwt_refresh'));

    const authenticate = async () => {
        if (storedUser?.username && refresh_token) {
            dispatch(setActiveRole(storedUser.activeRole));
            dispatch(setIsAuth(true));
            dispatch(setIsAuthLoading(true));

            const result = await authUser({
                username: storedUser.username,
                grant_type: 'refresh_token',
                refresh_token
            });

            if (result.error) {
                console.error(result.error);
                dispatch(setIsAuthLoading(false));
                navigate('/');
                return;
            }

            dispatch(setCredentials(result.data.access_token));
            localStorage.setItem('jwt_refresh', JSON.stringify(result.data.refresh_token));

            const user = await getUserInfo(
                {username: storedUser.username, role: storedUser.activeRole || result.data.roles[0]},
                {preferCacheValue: true}
            );

            if (user.error) {
                console.error(user.error);
                return;
            }

            const activeRole = user.data.user.roles.includes(storedUser.activeRole)
                ? storedUser.activeRole
                : user.data.user.roles[0];

            const updatedUser = {
                ...storedUser,
                activeRole,
                last_login_date: result.data.last_login_date   // <-- тоже last_login_date
            };

            dispatch(setUser(updatedUser));
            localStorage.setItem('user', JSON.stringify(updatedUser));
            localStorage.setItem('last_login', updatedUser.last_login_date);

            dispatch(setIsAuthLoading(false));
        } else if (!storedUser?.username) {
            dispatch(setIsAuthLoading(false));
            dispatch(logOut());
        }
    };

    const didRun = useRef(false);
    useEffect(() => {
        if (didRun.current || isAuth) return;
        didRun.current = true;
        authenticate();
    }, []);

};
